import { Component, ElementRef, HostListener, QueryList, ViewChildren, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MtrLinkModalService } from './mtr-link-modal.service';
import { AuthStorageService } from '../../../core/auth/services/auth-storage.service';

@Component({
  selector: 'app-mtr-link-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './mtr-link-modal.component.html',
  styleUrl: './mtr-link-modal.component.scss'
})
export class MtrLinkModalComponent {
  readonly modalService = inject(MtrLinkModalService);
  private readonly authStorage = inject(AuthStorageService);
  private readonly router = inject(Router);

  @ViewChildren('otpInput') otpInputRefs!: QueryList<ElementRef<HTMLInputElement>>;

  readonly otpIndices = [0, 1, 2, 3, 4, 5] as const;

  trackByIndex(index: number): number {
    return index;
  }

  // Step 1: 6-Digit OTP State
  otpDigits: string[] = ['', '', '', '', '', ''];
  readonly isShaking = signal<boolean>(false);
  private shakeTimeout: any = null;

  // Step 2: Password State
  password = '';
  readonly showPassword = signal<boolean>(false);

  readonly userEmail = computed(() => this.authStorage.currentUser()?.email || '');

  private readonly currentUrl = signal<string>(this.router.url);

  constructor() {
    this.router.events.subscribe(() => {
      this.currentUrl.set(this.router.url);
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event?: any): void {
    if (this.isVisible()) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      this.triggerStaticFeedback();
    }
  }

  readonly isRecoveryRoute = computed(() => {
    const url = this.currentUrl();
    return url.includes('/forgot-password') || url.includes('/reset-password');
  });

  readonly isVisible = computed(() => {
    return this.modalService.isOpen() && !this.isRecoveryRoute();
  });

  readonly currentStep = computed(() => this.modalService.currentStep());
  readonly isSendingOtp = computed(() => this.modalService.isSendingOtp());
  readonly isVerifyingOtp = computed(() => this.modalService.isVerifyingOtp());
  readonly isSubmitting = computed(() => this.modalService.isSubmitting());
  readonly otpError = computed(() => this.modalService.otpError());
  readonly passwordError = computed(() => this.modalService.passwordError());
  readonly resendCooldown = computed(() => this.modalService.resendCooldown());
  readonly errorMessage = computed(() => this.modalService.errorMessage());

  get fullOtp(): string {
    return this.otpDigits.join('');
  }

  get isOtpComplete(): boolean {
    return this.fullOtp.length === 6 && /^\d{6}$/.test(this.fullOtp);
  }

  triggerStaticFeedback(): void {
    this.isShaking.set(true);
    if (this.shakeTimeout) {
      clearTimeout(this.shakeTimeout);
    }
    this.shakeTimeout = setTimeout(() => {
      this.isShaking.set(false);
    }, 450);
  }

  onBackdropClick(): void {
    this.triggerStaticFeedback();
  }

  onOtpInput(event: any, index: number): void {
    const input = (event?.target || event) as HTMLInputElement;
    if (!input) return;

    let val = (input.value || '').replace(/\D/g, '');

    if (this.modalService.otpError()) {
      this.modalService.otpError.set(null);
    }

    if (val.length > 1) {
      // User typed or pasted multiple characters in a box
      const chars = val.split('');
      const inputArr = this.otpInputRefs?.toArray();
      for (let i = 0; i < chars.length && (index + i) < 6; i++) {
        this.otpDigits[index + i] = chars[i];
        if (inputArr && inputArr[index + i]) {
          inputArr[index + i].nativeElement.value = chars[i];
        }
      }
      const nextIndex = Math.min(index + chars.length, 5);
      this.focusOtpInput(nextIndex);
      return;
    }

    const singleDigit = val.slice(-1);
    this.otpDigits[index] = singleDigit;
    input.value = singleDigit;

    if (singleDigit && index < 5) {
      this.focusOtpInput(index + 1);
    }
  }

  onOtpKeyDown(event: any, index: number): void {
    const key = event?.key;

    if (key === 'Enter') {
      event?.preventDefault?.();
      if (this.isOtpComplete) {
        this.submitOtp();
      }
      return;
    }

    if (key === 'Backspace') {
      const input = (event?.target || event) as HTMLInputElement;
      if (!this.otpDigits[index] || input?.value === '') {
        // Current box is already empty -> move to previous box and clear it
        if (index > 0) {
          event?.preventDefault?.();
          this.otpDigits[index - 1] = '';
          const prevInput = this.otpInputRefs?.toArray()?.[index - 1]?.nativeElement;
          if (prevInput) {
            prevInput.value = '';
          }
          this.focusOtpInput(index - 1);
        }
      } else {
        // Clear current box
        this.otpDigits[index] = '';
        if (input) {
          input.value = '';
        }
      }
      return;
    }

    if (key === 'ArrowLeft' && index > 0) {
      event?.preventDefault?.();
      this.focusOtpInput(index - 1);
      return;
    }

    if (key === 'ArrowRight' && index < 5) {
      event?.preventDefault?.();
      this.focusOtpInput(index + 1);
      return;
    }

    // Overwrite existing digit if user enters another number
    if (/^\d$/.test(key)) {
      if (this.otpDigits[index]) {
        event?.preventDefault?.();
        this.otpDigits[index] = key;
        const input = (event?.target || event) as HTMLInputElement;
        if (input) {
          input.value = key;
        }
        if (index < 5) {
          this.focusOtpInput(index + 1);
        }
      }
    }
  }

  onOtpPaste(event: any): void {
    event?.preventDefault?.();
    const pasted = event?.clipboardData?.getData?.('text') || '';
    const digits = pasted.replace(/\D/g, '').slice(0, 6);
    if (!digits) return;

    const inputArr = this.otpInputRefs?.toArray();
    for (let i = 0; i < 6; i++) {
      const char = digits[i] || '';
      this.otpDigits[i] = char;
      if (inputArr && inputArr[i]) {
        inputArr[i].nativeElement.value = char;
      }
    }

    if (this.modalService.otpError()) {
      this.modalService.otpError.set(null);
    }

    const nextIndex = Math.min(digits.length, 5);
    this.focusOtpInput(nextIndex);
  }

  private focusOtpInput(index: number): void {
    const inputs = this.otpInputRefs?.toArray();
    if (inputs && inputs[index]) {
      inputs[index].nativeElement.focus();
    }
  }

  private syncOtpInputs(): void {
    const inputs = this.otpInputRefs?.toArray();
    if (!inputs) return;
    inputs.forEach((ref, idx) => {
      ref.nativeElement.value = this.otpDigits[idx] || '';
    });
  }

  submitOtp(): void {
    if (!this.isOtpComplete || this.isVerifyingOtp()) {
      return;
    }
    this.modalService.verifyOtp(this.fullOtp);
  }

  resendOtp(): void {
    if (this.resendCooldown() > 0 || this.isSendingOtp()) {
      return;
    }
    this.otpDigits = ['', '', '', '', '', ''];
    this.syncOtpInputs();
    this.modalService.sendOtp(true);
    this.focusOtpInput(0);
  }

  togglePassword(): void {
    this.showPassword.update((val) => !val);
  }

  onInput(): void {
    if (this.modalService.passwordError()) {
      this.modalService.passwordError.set(null);
    }
  }

  submit(): void {
    if (this.currentStep() === 'otp') {
      this.submitOtp();
    } else {
      this.submitPassword();
    }
  }

  submitPassword(): void {
    if (this.isSubmitting() || !this.password.trim()) {
      return;
    }
    this.modalService.linkAccount(this.password);
  }

  goToStep(step: 'otp' | 'password'): void {
    this.modalService.goToStep(step);
    if (step === 'otp') {
      setTimeout(() => this.focusOtpInput(0), 50);
    }
  }

  onForgotPassword(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    this.password = '';
    this.modalService.goToForgotPassword();
  }

  onLogout(): void {
    this.password = '';
    this.otpDigits = ['', '', '', '', '', ''];
    this.modalService.logout();
  }
}
